import { useEffect, useState } from 'react';
import oncehubLogo from '@/assets/images/oncehub_logo.png';
import useIntegrationsStore from '@/stores/IntegrationsStore';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { saveIntegrationSettings } from '@/hooks/integrations';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

const OnceHubSettings = () => {
  const { oncehub, loading } = useIntegrationsStore(
    useShallow((state) => ({
      oncehub: state.oncehub,
      loading: state.loading,
    }))
  );

  const [apiKey, setApiKey] = useState('');
  const [bookingPageId, setBookingPageId] = useState('');
  const [eventTypeId, setEventTypeId] = useState('');
  const [saving, setSaving] = useState(false);

  // Load existing config from store
  useEffect(() => {
    if (oncehub) {
      if (oncehub.default_booking_page_id) {
        setBookingPageId(oncehub.default_booking_page_id);
      }
      if (oncehub.default_event_type_id) {
        setEventTypeId(oncehub.default_event_type_id);
      }
    }
  }, [oncehub]);

  const isActive = oncehub?.active === true;

  const handleSave = async () => {
    if (!apiKey && !bookingPageId && !eventTypeId) {
      toast.error(
        'Please provide at least the API key or a default booking page.'
      );
      return;
    }

    const settings = {};
    if (apiKey) settings.api_key = apiKey;
    if (bookingPageId) settings.default_booking_page_id = bookingPageId;
    if (eventTypeId) settings.default_event_type_id = eventTypeId;

    setSaving(true);
    try {
      await saveIntegrationSettings('oncehub', settings);
      toast.success('OnceHub settings saved successfully');
    } catch (error) {
      console.error('Failed to save OnceHub settings:', error);
      toast.error(
        error.response?.data?.message || 'Failed to save OnceHub settings'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center pt-48'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  return (
    <div className='h-full space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>
            OnceHub (ScheduleOnce) Integration
          </h3>
          <p className='text-sm text-muted-foreground'>
            Connect your OnceHub / ScheduleOnce account using an API key and
            configure default booking settings.
          </p>
        </div>
      </div>
      <Separator />

      <div className='space-y-6'>
        {/* Connection status */}
        <Card>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <div className='rounded-lg bg-gray-100 p-2'>
                  <img
                    src={oncehubLogo}
                    alt='OnceHub'
                    className='h-6 w-6 object-contain'
                  />
                </div>
                <div>
                  <CardTitle>OnceHub</CardTitle>
                  <CardDescription>
                    {isActive ? 'Connected' : 'Not connected'}
                  </CardDescription>
                </div>
              </div>
              <div className='flex items-center gap-2 rounded-full px-3 py-1'>
                {isActive ? (
                  <>
                    <CheckCircle2 className='h-4 w-4 text-green-600' />
                    <span className='text-sm font-medium text-green-600'>
                      Connected
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className='h-4 w-4 text-gray-500' />
                    <span className='text-sm font-medium text-gray-500'>
                      Not connected
                    </span>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Settings form */}
        <Card>
          <CardHeader>
            <CardTitle>Configuration</CardTitle>
            <CardDescription>
              Add your OnceHub API key and optionally configure default booking
              page and event type IDs. Refer to your OnceHub dashboard or the
              OnceHub Developer Center for these values.
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <Alert>
              <AlertDescription>
                API key is stored encrypted in your customer configuration. If
                you rotate it in OnceHub, remember to update it here.
              </AlertDescription>
            </Alert>

            <div className='space-y-2'>
              <Label htmlFor='oncehub-api-key'>OnceHub API Key</Label>
              <Input
                id='oncehub-api-key'
                type='password'
                placeholder='Paste your OnceHub API key'
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='oncehub-booking-page'>
                Default Booking Page ID (optional)
              </Label>
              <Input
                id='oncehub-booking-page'
                placeholder='e.g. page_123'
                value={bookingPageId}
                onChange={(e) => setBookingPageId(e.target.value)}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='oncehub-event-type'>
                Default Event Type ID (optional)
              </Label>
              <Input
                id='oncehub-event-type'
                placeholder='e.g. event_456'
                value={eventTypeId}
                onChange={(e) => setEventTypeId(e.target.value)}
              />
            </div>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnceHubSettings;
